import {
  AdvancedFilterData,
  FilterGroup,
  filtersAreEqual,
  getFilterBuilder,
} from "../advanced-filters/advanced-filter-data";
import {
  decodeFilterGroup,
  isFilterGroup,
} from "../advanced-filters/generic-advanced-filter-data";

export function isFilterPartial(filter: AdvancedFilterData): boolean {
  const filterBuilder = getFilterBuilder(filter);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return filterBuilder.isPartial(filter as any);
}

export function isFilterGroupValid(group: FilterGroup | undefined): boolean {
  if (!group) {
    return true;
  }

  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      if (!isFilterGroupValid(clause)) {
        return false;
      }
    } else if (isFilterPartial(clause)) {
      return false;
    }
  }

  return true;
}

export function filterGroupsAreEqual(
  group1: FilterGroup | undefined,
  group2: FilterGroup | undefined,
): boolean {
  if (!group1 && !group2) {
    return true;
  }
  if (!group1 || !group2) {
    return false;
  }
  if (group1.operator !== group2.operator) {
    return false;
  }
  if (group1.clauses.length !== group2.clauses.length) {
    return false;
  }

  for (let i = 0; i < group1.clauses.length; i++) {
    const clause1 = group1.clauses[i];
    const clause2 = group2.clauses[i];

    const isGroup1 = isFilterGroup(clause1);
    const isGroup2 = isFilterGroup(clause2);

    if (isGroup1 !== isGroup2) {
      return false;
    }

    if (isGroup1 && isGroup2) {
      if (!filterGroupsAreEqual(clause1, clause2)) {
        return false;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    } else if (!isGroup1 && !isGroup2 && !filtersAreEqual(clause1, clause2)) {
      return false;
    }
  }

  return true;
}

/**
 * Creates a leaf filter decoder function for use with decodeFilterGroup.
 * This consolidates the common logic for parsing "name@@dataString" encoded filters.
 *
 * @param getFilterDataByName - Function that returns the default filter data for a given filter name
 * @returns A decoder function that can be passed to decodeFilterGroup
 */
export function createLeafFilterDecoder(
  getFilterDataByName: (name: string) => AdvancedFilterData | undefined,
): (filterStr: string) => AdvancedFilterData | null {
  return (filterStr: string): AdvancedFilterData | null => {
    const [name, dataString] = filterStr.split("@@");
    if (!name || dataString === undefined) {
      return null;
    }

    const defaultFilter = getFilterDataByName(name);
    if (!defaultFilter) {
      return null;
    }

    const filterBuilder = getFilterBuilder(defaultFilter);
    const data = filterBuilder.readFromString(name, dataString, defaultFilter);
    return data ?? null;
  };
}

/**
 * Decodes a URL-encoded filter group string into a FilterGroup object.
 *
 * @param encoded - The encoded filter group string
 * @param getFilterDataByName - Function that returns the default filter data for a given filter name
 * @returns The decoded FilterGroup or null if parsing fails
 */
export function decodeAdvancedFilterGroup(
  encoded: string | undefined,
  getFilterDataByName: (name: string) => AdvancedFilterData | undefined,
): FilterGroup | null {
  if (!encoded) {
    return null;
  }

  return decodeFilterGroup<AdvancedFilterData>(
    encoded,
    createLeafFilterDecoder(getFilterDataByName),
  );
}
